import {
  CardMedia,
  Typography,
  useTheme,
} from '@mui/material';
import Box from '@mui/material/Box';
import {
  ReactNode,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import { Content, MetaItem } from '../../../common/models/content/Content';
import { getFormattedDate } from '../../../common/utils/DateUtils';
import { getFormattedTestId } from './ContentCommonFunctions';
import { TestIds } from '../../../assets/testIds';
import { useIsDesktop, useIsSmallScreen } from '../../../common/hooks/useResponsive';
import ContentArticleBodyView from './ContentArticleBodyView';
import ContentArticleHighlights from './ContentArticleHighlights';
import PlaybookGoal from './PlaybookGoal';
import { axiosInstance } from '../../../api/apiConfig';
import { colorTokens } from '../../../assets/primitiveColorTokens';

interface ArticleContentDisplayProps {
  content: Content;
  isPreview?: boolean;
  // Save/Share buttons. Shown beside the byline on desktop and on their own row on mobile.
  actions?: ReactNode;
}

/**
 * Displays article content (topics, title, description, byline, image, body).
 * Shared by the article page and the dialogs/previews.
 */
export function ArticleContentDisplay({
  content, isPreview = false, actions,
}: ArticleContentDisplayProps) {
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const isSmallScreen = useIsSmallScreen();

  const containerRef = useRef<HTMLDivElement>(null);

  // intercept clicks on private resource links and open the S3 signed url
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a');
      if (link && link.classList.contains('private-resource-link')) {
        e.preventDefault();
        axiosInstance.get(link.href).then((res) => {
          window.open(res.data.signedUrl, '_blank');
        });
      }
    };
    const container = containerRef.current;
    container?.addEventListener('click', handleClick);
    return () => { container?.removeEventListener('click', handleClick); };
  }, []);

  // Only the Airtable source publish date is shown, never a system date
  const publishedDate = useMemo(() => {
    const displayDate = content?.attributes?.source_published_date;
    return displayDate ? getFormattedDate(displayDate) : '';
  }, [content]);

  const articleTopics = useMemo(() => {
    const metaData = content?.attributes?.metadata || [];
    if (metaData.length === 0) return [];
    return metaData.filter((item: MetaItem) => item.category.toLowerCase() === 'topic');
  }, [content]);

  const author = content?.attributes?.author?.trim();
  const shortDescription = content?.attributes?.short_description?.trim();
  const hero = content?.attributes?.hero?.data?.attributes;
  const highlights = content?.attributes?.highlights;
  const highlightsPosition = highlights?.position ?? 'after_intro';
  const isPlaybook = content?.attributes?.type === 'playbook';
  // Playbooks don't show an author or date
  const hasByline = !isPlaybook && (!!author || !!publishedDate);

  const actionsRow = actions && (
    <Box sx={{
      display: 'flex',
      gap: theme.spacing(1),
      flexShrink: 0,
      // Keeps the buttons right-aligned even when the article has no author or date
      ml: isSmallScreen ? 0 : 'auto',
    }}>
      {actions}
    </Box>
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2.5),
        pt: isDesktop && isPreview ? theme.spacing(10) : 0,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1.5) }}>
        {articleTopics.length > 0 &&
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(0.75) }}>
            {articleTopics.map((item, index) => (
              <Box
                data-testid={`${TestIds.contentArticleView.contentTopic}-${getFormattedTestId(item.name)}`}
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: theme.spacing(1),
                  py: theme.spacing(0.25),
                  borderRadius: theme.spacing(1250),
                  // The first topic is the primary one and is emphasised
                  backgroundColor: index === 0 ?
                    colorTokens.color.brand.everPro[50] : colorTokens.color.gray[50],
                  border: `1px solid ${index === 0 ?
                    colorTokens.color.brand.everPro[200] : colorTokens.color.gray[200]}`,
                }}
              >
                <Typography sx={{
                  fontSize: theme.spacing(1.5),
                  lineHeight: theme.spacing(2.25),
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                }}>
                  {item.name}
                </Typography>
              </Box>
            ))}
          </Box>
        }
        <Typography
          component='h1'
          data-testid={TestIds.contentArticleView.contentTitle}
          sx={{
            fontSize: theme.spacing(4),
            lineHeight: 1.235,
            letterSpacing: '0.25px',
            fontWeight: 400,
            color: theme.palette.text.primary,
          }}
        >
          {content?.attributes?.title}
        </Typography>
        {highlightsPosition === 'after_title' && <ContentArticleHighlights highlights={highlights} />}
        {shortDescription &&
          <Typography sx={{
            fontSize: theme.spacing(2),
            lineHeight: 1.5,
            letterSpacing: '0.15px',
            color: theme.palette.text.secondary,
          }}>
            {shortDescription}
          </Typography>
        }
        {highlightsPosition === 'after_intro' && <ContentArticleHighlights highlights={highlights} />}
        {(hasByline || actionsRow) &&
          <Box sx={{
            display: 'flex',
            // Save/Share sit right of the byline; they only drop to their own row on phones
            flexDirection: isSmallScreen ? 'column' : 'row',
            alignItems: isSmallScreen ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            // On phones the byline and buttons are two 10px-padded rows 12px apart
            gap: isSmallScreen ? theme.spacing(4) : theme.spacing(2.5),
            py: theme.spacing(1.25),
          }}>
            {hasByline &&
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(1),
                // Same height with or without the avatar so the row doesn't shift
                minHeight: theme.spacing(3),
              }}>
                {/* Avatar placeholder until author images are available */}
                {author &&
                  <Box sx={{
                    width: theme.spacing(3),
                    height: theme.spacing(3),
                    borderRadius: '50%',
                    flexShrink: 0,
                    backgroundColor: colorTokens.color.gray[300],
                  }} />
                }
                <Typography sx={{
                  fontSize: theme.spacing(1.5),
                  lineHeight: 1.66,
                  letterSpacing: '0.4px',
                  color: theme.palette.text.secondary,
                }}>
                  {author &&
                    <span data-testid={TestIds.contentArticleView.contentAuthorName}>
                      By {author}
                    </span>
                  }
                  {author && publishedDate && ' · '}
                  {publishedDate &&
                    <span data-testid={TestIds.contentArticleView.contentPublishedDate}>
                      {publishedDate}
                    </span>
                  }
                </Typography>
              </Box>
            }
            {actionsRow}
          </Box>
        }
      </Box>
      {highlightsPosition === 'after_byline' && <ContentArticleHighlights highlights={highlights} />}
      {/* Without a hero the body follows the header with the same 20px rhythm */}
      {hero &&
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.75) }}>
          <CardMedia
            sx={{
              height: theme.spacing(47.375),
              width: '100%',
              borderRadius: theme.spacing(0.75),
            }}
            component='div'
            role='img'
            aria-label={hero.alternativeText || undefined}
            image={hero.url}
          />
          {hero.caption &&
            <Typography sx={{
              fontSize: theme.spacing(1.5),
              lineHeight: 1.66,
              letterSpacing: '0.4px',
              color: theme.palette.text.secondary,
            }}>
              {hero.caption}
            </Typography>
          }
        </Box>
      }
      {isPlaybook ?
        // Playbooks narrow the body to a centered 1104px column under the full-width hero
        <Box
          data-testid={TestIds.playbookView.bodyColumn}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2.5),
            width: '100%',
            maxWidth: theme.spacing(138),
            mx: 'auto',
          }}
        >
          <ContentArticleBodyView richText={content?.attributes?.body} />
          <PlaybookGoal goal={content.attributes.playbook_goal} />
        </Box> :
        <ContentArticleBodyView richText={content?.attributes?.body} />
      }
    </Box>
  );
}
